import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Text } from '@asigna/kit';
import { createMultisignWallet, getAllMyMultisigs, getPendingMultisigs } from 'api';
import { AxiosError } from 'axios';
import { NotificationCard } from 'components';
import { CREATE_DUPLICATE_MULTISIG } from 'constants/errors';
import useNetwork from 'hooks/use-network';
import { ReactComponent as ErrorTriangle } from 'images/errorTriangle.svg';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { toast } from 'sonner';
import { createdSafeAtom, usePendingSafes, useSafes } from 'store';
import { StxChain, StxChainEnum } from 'types';
import { Owner } from 'types/createSafeTypes';
import { shortenAddress } from 'utils/helper';

interface StepThreeProps {
  walletAccount: string;
  safeName: string;
  confirmationThreshold: number;
  owners: Owner[];
  onBackClick: () => void;
  errorStatus: string;
  setCreateSafeError: (error: string) => void;
}

const ALREADY_EXISTS_MSG = 'Draft vault already exists';
const ALREADY_EXIST_DISPLAY_MSG =
  'Please note that a vault with identical parameters (same number of signers, identical signer addresses, and threshold) cannot be created.';
const DEFAULT_DISPLAY_MSG = 'Failed to create Vault due to a network error.';

export const StepThree: React.FC<StepThreeProps> = ({
  walletAccount,
  safeName,
  confirmationThreshold,
  owners,
  onBackClick,
  errorStatus,
  setCreateSafeError,
}) => {
  const history = useHistory();
  // const fetchPendingSafes = useSetAtom(fetchPendingSafesAtom);
  // const fetchSafes = useSetAtom(fetchSafesAtom);

  const [, setCreatedSafe] = useAtom(createdSafeAtom);
  const [network] = useNetwork();
  const net = StxChainEnum[network] as any as StxChain;
  const { setSafes } = useSafes();
  const { setPendingSafes } = usePendingSafes();

  const [createClicked, setCreateClicked] = useState(false);

  const handleConfirm = async () => {
    setCreateClicked(true);
    try {
      if (isEmpty(walletAccount)) {
        // TODO: нужно оповещение юзера тут
        return;
      }
      await createMultisignWallet({
        name: safeName,
        owners: owners.map((owner) => ({ address: owner.address.trim() })),
        threshold: confirmationThreshold,
        network: net,
      })
        .then(async () => {
          const isPending = owners.length > 1;
          if (isPending) {
            const pendingMulitisgsResponse = await getPendingMultisigs(net);

            setPendingSafes(pendingMulitisgsResponse);
          } else {
            const multisigsResponse = await getAllMyMultisigs(net);
            setSafes(multisigsResponse);
          }
          toast(
            () => (
              <NotificationCard
                title={isPending ? 'Vault Created' : 'Vault Draft Created'}
                message={
                  isPending
                    ? 'Vault created successfully.'
                    : 'Vault draft created. It will activate once all signers join.'
                }
                type="success"
              />
            ),
            {
              className: 'w-full',
            }
          );

          history.push('/');
        })
        .catch((error) => {
          if (error instanceof AxiosError) {
            if (error.response) {
              const errorMessage =
                error.response.data.message === ALREADY_EXISTS_MSG
                  ? ALREADY_EXIST_DISPLAY_MSG
                  : DEFAULT_DISPLAY_MSG;
              setCreateSafeError(errorMessage);
              toast(
                () => (
                  <NotificationCard
                    title="Vault Creation Failed"
                    message={errorMessage}
                    type="error"
                  />
                ),
                {
                  className: 'w-full',
                }
              );
              console.error('An error occurred:', error.response.data.message);
            } else {
              console.error('An unexpected error occurred:', error);
            }
          } else {
            console.error('An unexpected error occurred:', error);
          }
        });
      // setCreatedSafe({
      //   option:
      //     owners.length === 1
      //       ? SuccessModalType.SAFE_CREATED
      //       : SuccessModalType.SAFE_MULTIPLE_OWNER_CREATED,
      //   name: safeName,
      // });
      // history.push('/');
    } catch (error: any) {
      const errorMessage = error.response.data.message;
      setCreateSafeError(errorMessage);
      setCreatedSafe(null);
      // TODO: handle possible errors visually on safe creation
      console.error('An error occurred:', errorMessage);
    } finally {
      setCreateClicked(false);
    }
  };

  return (
    <div className="flex flex-col gap-24 max-w-[592px] w-full mx-auto">
      <Text message="Review" color="white" size="r20" />
      <div className="flex flex-col gap-24 rounded-[12px] border border-[.5px] border-black-4 bg-black-2 p-16 sm:p-24 mb-32">
        {errorStatus === CREATE_DUPLICATE_MULTISIG && (
          <div className="flex gap-12">
            <ErrorTriangle />
            <Text
              message="Identical vault already exists. Start creation over again and follow warning messages."
              color="error"
              size="r13"
            />
          </div>
        )}
        <div className="flex flex-col gap-8">
          <Text message="Vault Name" color="regular" size="m14" />
          <p className="text-m14 text-white break-all">{safeName}</p>
        </div>
        <div className="h-[.5px] bg-black-4 mx-[-24px]" />
        <div className="flex flex-col gap-16">
          <Text message="Added Owners" color="white" size="m14" />
          <div className="flex flex-col gap-12">
            {owners.map(({ address, name }) => (
              <div
                className="flex items-center justify-between rounded-[6px] px-16 py-4 bg-white-3 h-36 hover:cursor-text"
                key={address}
              >
                <div className="grid grid-cols-2 justify-between gap-4 flex-1 items-center">
                  <p className="text-r13 text-white">{shortenAddress(address)}</p>
                  <p className="text-m13 text-regular">{name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="h-[.5px] bg-black-4 mx-[-24px]" />
        <div className="">
          <Text message="Confirmation threshold" color="regular" size="m14" />
          <Text
            message={`${confirmationThreshold} out of ${owners.length} owners`}
            color="white"
            size="m14"
          />
        </div>
        <div className="flex gap-16 justify-end">
          <Button
            colorType="secondary"
            onClick={onBackClick}
            message="Back"
            className="w-[128px]"
          />
          <Button
            colorType="primary"
            onClick={handleConfirm}
            disabled={errorStatus.length > 0 || createClicked}
            message="Create"
            className="w-[128px]"
          />
        </div>
      </div>
    </div>
  );
};
